import { CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, TemplateRef, inject } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective } from '@angular/forms';
import { ClrButtonGroupModule, ClrIconModule } from '@clr/angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';

import { UtilsModule } from '@mysvg/utils';
import { ErrorModule } from '@svg-frontends/error';
import { FormModule, SvgFrontendsValidators } from '@svg-frontends/form';
import { ProgressSpinnerModule } from '@svg-frontends/progress-spinner';

import { AbstractControlComponent } from '../../classes/AbstractControlComponent';
import { FormStatus } from '../../enums/form-status.enum';
import { FormGroupService } from '../../services/form-group.service';

@UntilDestroy()
@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'svg-frontends-list-form-control',
	styleUrls: ['./list-form-control.component.scss'],
	templateUrl: './list-form-control.component.html',
	viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
	imports: [ProgressSpinnerModule, FormModule, NgClass, ClrIconModule, ErrorModule, CommonModule, UtilsModule, ClrButtonGroupModule],
})
export class ListFormControlComponent extends AbstractControlComponent implements OnInit {
	private changeDetectorRef = inject(ChangeDetectorRef);
	private formGroupDirective = inject(ControlContainer);
	private macFormGroupService = inject(FormGroupService);

	FORM_STATUS = FormStatus;

	formControl: FormControl;
	inputFormControl: FormControl = new FormControl();

	private items: string[] = [];
	private editIndex: number | null = null;
	private itemSubject$ = new BehaviorSubject<string[]>([]);
	items$ = this.itemSubject$.asObservable();
	editingIndex$ = new BehaviorSubject<number | null>(null);

	@Input() controlName: string;
	@Input() disableControl: boolean = undefined;
	@Input() helperText?: string | TemplateRef<any>;
	@Input() label?: string | TemplateRef<any>;
	@Input() placeholder = '';
	@Input() type = 'text';
	@Input() readOnly?: boolean;
	@Input() removeWhitespaceOnPaste: boolean = false; // the default behavior is to not remove whitespace on paste

	ngOnInit(): void {
		this.formControl = this.formGroupDirective.control.get(this.controlName) as FormControl;
		if (this.type === 'email') {
			this.inputFormControl.addValidators(SvgFrontendsValidators.isEmail());
		}
		this.macFormGroupService.submit$.pipe(untilDestroyed(this)).subscribe(() => {
			FormGroupService.touchControl(this.inputFormControl);
			FormGroupService.touchControl(this.formControl);
			this.changeDetectorRef.markForCheck();
		});
		this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			this.itemSubject$.next(value);
			this.items = value;
		});
	}

	addItem() {
		if (this.inputFormControl.valid && this.inputFormControl.value && this.inputFormControl.value.trim() !== '') {
			if (this.editIndex !== null) {
				this.items[this.editIndex] = this.inputFormControl.value;
				this.editIndex = null;
				this.editingIndex$.next(null);
			} else if (!this.items.includes(this.inputFormControl.value)) {
				this.items.push(this.inputFormControl.value);
			}
			this.formControl.setValue(this.items);
			this.inputFormControl.reset();
		}
	}

	removeItem(index: number) {
		this.items.splice(index, 1);
		if (this.editIndex === index) {
			this.editIndex = null;
			this.editingIndex$.next(null);
			this.inputFormControl.reset();
		}
		this.formControl.setValue(this.items);
	}

	editItem(index: number) {
		this.inputFormControl.setValue(this.items[index]);
		this.editIndex = index;
		this.editingIndex$.next(index);
	}

	onPaste($event: ClipboardEvent): void {
		if (this.removeWhitespaceOnPaste) {
			const pasteString = $event?.clipboardData?.getData('text/plain');
			if (pasteString) {
				this.inputFormControl.setValue(pasteString.trim());
				$event.preventDefault();
			}
		}
	}
}
